import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { FileInput, FileOutput, ClipboardList, CreditCard, TrendingUp, Clock } from 'lucide-react';
import api, { formatCurrency, formatDate } from '@/lib/api';

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/dashboard/stats').then(r => { setStats(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64 text-muted-foreground">Loading...</div>;
  if (!stats) return <div className="text-muted-foreground">Failed to load dashboard</div>;

  const cards = [
    { title: 'Accounts Payable', value: formatCurrency(stats.total_payable), sub: `${stats.count_payable} invoices | ${stats.pending_payable} pending`, icon: FileInput, color: 'text-amber-400', bg: 'bg-amber-500/10' },
    { title: 'Accounts Receivable', value: formatCurrency(stats.total_receivable), sub: `${stats.count_receivable} invoices | ${stats.pending_receivable} outstanding`, icon: FileOutput, color: 'text-blue-400', bg: 'bg-blue-500/10' },
    { title: 'Purchase Orders', value: formatCurrency(stats.total_po), sub: `${stats.count_po} orders | ${stats.approved_po} approved`, icon: ClipboardList, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
    { title: 'Total Payments', value: formatCurrency(stats.total_payments), sub: `${stats.count_payments} transactions recorded`, icon: CreditCard, color: 'text-violet-400', bg: 'bg-violet-500/10' },
  ];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard-page">
      <div>
        <h1 className="text-2xl font-bold text-foreground tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Dashboard</h1>
        <p className="text-sm text-muted-foreground mt-1">Financial overview for GO MO Group DBS</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((card, i) => (
          <Card key={card.title} className={`stat-card bg-card border-border animate-fade-in-up stagger-${i + 1}`} data-testid={`stat-card-${i}`}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">{card.title}</CardTitle>
              <div className={`${card.bg} ${card.color} p-2 rounded-md`}><card.icon size={16} /></div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-foreground" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{card.value}</div>
              <p className="text-xs text-muted-foreground mt-1">{card.sub}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Payable */}
        <Card className="bg-card border-border" data-testid="recent-payable-card">
          <CardHeader className="flex flex-row items-center gap-2 pb-3">
            <Clock size={16} className="text-muted-foreground" />
            <CardTitle className="text-sm font-medium text-card-foreground/80">Recent Vendor Invoices</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table className="data-table">
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead className="text-muted-foreground">Invoice #</TableHead>
                  <TableHead className="text-muted-foreground">Vendor</TableHead>
                  <TableHead className="text-muted-foreground">Amount</TableHead>
                  <TableHead className="text-muted-foreground">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(stats.recent_payable || []).length === 0 ? (
                  <TableRow className="border-border"><TableCell colSpan={4} className="text-center text-muted-foreground py-6">No invoices yet</TableCell></TableRow>
                ) : stats.recent_payable.map(inv => (
                  <TableRow key={inv.id} className="border-border hover:bg-muted/50">
                    <TableCell className="text-card-foreground/80 font-mono text-xs">{inv.invoice_number}</TableCell>
                    <TableCell className="text-card-foreground/80">{inv.vendor_name}</TableCell>
                    <TableCell className="text-foreground font-mono">{formatCurrency(inv.amount, inv.currency)}</TableCell>
                    <TableCell><StatusBadge status={inv.status} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Recent Receivable */}
        <Card className="bg-card border-border" data-testid="recent-receivable-card">
          <CardHeader className="flex flex-row items-center gap-2 pb-3">
            <TrendingUp size={16} className="text-muted-foreground" />
            <CardTitle className="text-sm font-medium text-card-foreground/80">Recent Client Invoices</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table className="data-table">
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead className="text-muted-foreground">Invoice #</TableHead>
                  <TableHead className="text-muted-foreground">Client</TableHead>
                  <TableHead className="text-muted-foreground">Amount</TableHead>
                  <TableHead className="text-muted-foreground">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(stats.recent_receivable || []).length === 0 ? (
                  <TableRow className="border-border"><TableCell colSpan={4} className="text-center text-muted-foreground py-6">No invoices yet</TableCell></TableRow>
                ) : stats.recent_receivable.map(inv => (
                  <TableRow key={inv.id} className="border-border hover:bg-muted/50">
                    <TableCell className="text-card-foreground/80 font-mono text-xs">{inv.invoice_number}</TableCell>
                    <TableCell className="text-card-foreground/80">{inv.client_name}</TableCell>
                    <TableCell className="text-foreground font-mono">{formatCurrency(inv.amount, inv.currency)}</TableCell>
                    <TableCell><StatusBadge status={inv.status} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  return (
    <Badge variant="outline" className={`status-${status} text-xs capitalize`} data-testid={`status-badge-${status}`}>
      {status}
    </Badge>
  );
}
